import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface User {
  id: string;
  email: string;
  display_name: string;
  is_superuser: boolean;
  totp_enabled: boolean;
}

export interface WorkspaceMembership {
  workspace_id: string;
  workspace_slug: string;
  workspace_name: string;
  role: 'owner' | 'admin' | 'viewer';
}

export interface AuthSession {
  access_token: string;
  user: User;
  workspaces: WorkspaceMembership[];
}

interface AuthState {
  accessToken: string | null;
  user: User | null;
  workspaces: WorkspaceMembership[];
  currentWorkspace: WorkspaceMembership | null;
  setLogin: (data: AuthSession) => void;
  setAccessToken: (t: string) => void;
  setCurrentWorkspace: (ws: WorkspaceMembership) => void;
  logout: (redirectTo?: string) => Promise<void>;
}

export const useAuth = create<AuthState>()(
  persist(
    (set) => ({
      accessToken: null,
      user: null,
      workspaces: [],
      currentWorkspace: null,
      setLogin: ({ access_token, user, workspaces }) =>
        set((state) => ({
          accessToken: access_token,
          user,
          workspaces,
          // A refresh response carries the latest memberships. Keep the
          // selected workspace when it still exists instead of jumping back
          // to the first workspace at every token rotation.
          currentWorkspace:
            workspaces.find(
              (workspace) =>
                workspace.workspace_id === state.currentWorkspace?.workspace_id,
            ) ?? workspaces[0] ?? null,
        })),
      setAccessToken: (accessToken) => set({ accessToken }),
      setCurrentWorkspace: (currentWorkspace) => set({ currentWorkspace }),
      logout: async (redirectTo) => {
        try {
          await fetch('/api/v1/auth/logout', {
            method: 'POST',
            credentials: 'include',
          });
        } catch {
          /* ignore */
        }
        set({
          accessToken: null,
          user: null,
          workspaces: [],
          currentWorkspace: null,
        });
        // Drop the refresh cookie too — server clears it on logout, but be safe.
        document.cookie = 'vodvault_refresh=; Max-Age=0; path=/';
        // Use a hard navigation so query cache and ephemeral state are also wiped.
        if (typeof window !== 'undefined') {
          const target = redirectTo?.startsWith('/') && !redirectTo.startsWith('//')
            ? redirectTo
            : '/login';
          window.location.href = target;
        }
      },
    }),
    {
      name: 'vodvault.auth',
      // Only persist what we need to rehydrate; the refresh cookie is httpOnly.
      partialize: (s) => ({
        accessToken: s.accessToken,
        user: s.user,
        workspaces: s.workspaces,
        currentWorkspace: s.currentWorkspace,
      }),
    },
  ),
);

// Zustand persist writes the refreshed token to localStorage, but it does not
// automatically update an already-open peer tab. Rehydrate on storage events
// so tabs converge on the newest rotated access token (and on logout).
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key === 'vodvault.auth') {
      void useAuth.persist.rehydrate();
    }
  });
}
